/**
 * PagosDivididos — un cobro, dos aseguradoras.
 *
 * ES EL CASO QUE MÁS SE ROMPE EN SILENCIO (§4 del spec): el cargo cuadra y el giro
 * no, porque la plata tenía que salir partida y salió entera por una pata. Nada
 * en la pantalla lo mostraba.
 *
 * NUNCA SE COLAPSA EN UNA LÍNEA (R12). El desglose de las dos patas va siempre a
 * la vista, cada una con su compañía, su monto y su estado de giro. Sumarlas
 * mostraría un cobro que cuadra y escondería que la mitad no salió.
 *
 * TRES ESTADOS DE GIRO Y NO DOS (R13): girado, GIRO PARCIAL, sin girar. El del
 * medio es el que no existía y es exactamente donde se pierde la plata.
 *
 * VA APARTE Y NO COMO UNA MARCA EN LA LISTA GRANDE (R15) porque pide una acción
 * distinta: una fila normal que no cuadra se resuelve con el proveedor; ésta se
 * resuelve girando la pata que falta.
 *
 * @see payment/src/services/transacciones-dia.service.ts — el reparto y los estados
 */
import React from 'react';

import { Badge } from './cierre/ds';
import { CIFRA } from '../../../lib/yoolpay/estados';
import { fmtClp } from '../../../lib/yoolpay/formato';

/** Estado del giro de UNA pata. Verde es un hecho; ámbar es que falta. */
function pataEstado(destino, filaEstado) {
  if (filaEstado === 'informativo') {
    return { texto: 'cobra por su cuenta', color: CIFRA.apagado, borde: 'var(--cw-borde)', fondo: '#fff' };
  }
  if (filaEstado === 'no_aplica') {
    return { texto: 'sin cobrar', color: CIFRA.apagado, borde: 'var(--cw-borde)', fondo: '#fff' };
  }
  return destino.girada
    ? { texto: 'girado', color: CIFRA.verde, borde: '#B9E2CD', fondo: 'var(--cw-done-bg)' }
    : { texto: 'sin girar', color: CIFRA.ambar, borde: '#EBC77A', fondo: 'var(--cw-obs-bg)' };
}

export default function PagosDivididos({ transacciones }) {
  if (!transacciones) return null;
  const splits = transacciones.filas.filter((f) => f.dividido);

  // No se muestra el bloque vacío. Un día sin cobros divididos no tiene nada que
  // revisar acá, y una tarjeta en cero invita a buscar un problema que no hay.
  if (splits.length === 0) return null;

  const parciales = transacciones.divididas_parciales;

  return (
    <div className="cw-card">
      <div style={{ padding: '19px 24px 15px', borderBottom: '1px solid var(--cw-linea)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 className="cw-card-title">Pagos divididos entre compañías</h2>
          <Badge variant={parciales > 0 ? 'pendiente' : 'recomendado'} soft ring>
            {splits.length} {splits.length === 1 ? 'cobro' : 'cobros'}
            {parciales > 0 ? ` · ${parciales} a medio girar` : ''}
          </Badge>
        </span>
        <p className="cw-card-sub">
          Un solo cobro al cliente que se reparte entre dos aseguradoras distintas. Es el
          caso que más se rompe en silencio: el cargo cuadra, el giro no, porque la plata
          tenía que salir partida. Van aparte y con su desglose a la vista — nunca sumados
          como una línea sola.
        </p>
      </div>

      {splits.map((s, i) => {
        const aMedias = s.estado_giro === 'parcial';
        return (
          <div
            key={s.cargo_id ?? i}
            style={{
              padding: '16px 24px',
              borderBottom: i === splits.length - 1 ? 'none' : '1px solid var(--cw-linea-fila)',
              background: aMedias ? 'var(--cw-obs-bg)' : '#fff',
            }}
          >
            <span
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 16, flexWrap: 'wrap',
              }}
            >
              <span style={{ minWidth: 0 }}>
                <span
                  style={{ display: 'block', fontSize: 13.5, fontWeight: 600, letterSpacing: '-.01em' }}
                >
                  {s.cliente_nombre ?? 'Cliente sin nombre'}
                </span>
                {/* El id completo en el `title`: es lo que se pega en un ticket
                    al proveedor, así que no se oculta del todo (R21). */}
                <span
                  className="cw-mono"
                  title={s.psp_charge_id ?? s.cargo_id ?? ''}
                  style={{
                    display: 'block', fontSize: 11.5,
                    color: 'var(--cw-tinta-suave)', marginTop: 3,
                  }}
                >
                  {s.suscripcion_codigo ?? s.cargo_id} · {s.plan ?? 'sin plan'}
                </span>
              </span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 9, flexShrink: 0 }}>
                <span className="cw-th">Cobro único</span>
                <span className="cw-mono" style={{ fontSize: 16, fontWeight: 700 }}>
                  {fmtClp(s.monto_clp)}
                </span>
              </span>
            </span>

            <span
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(s.destinos.length, 2)}, 1fr)`,
                gap: 10,
                marginTop: 12,
              }}
            >
              {s.destinos.map((d) => {
                const e = pataEstado(d, s.estado_giro);
                return (
                  <span
                    key={d.compania_id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 12, padding: '11px 14px', border: `1px solid ${e.borde}`,
                      background: e.fondo, borderRadius: 11,
                    }}
                  >
                    <span style={{ minWidth: 0 }}>
                      <span
                        title={d.compania_id}
                        style={{
                          display: 'block', fontSize: 12.5, fontWeight: 600,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {d.compania_nombre ?? d.compania_id}
                      </span>
                      <span
                        style={{ display: 'block', fontSize: 11.5, color: 'var(--yool-muted)', marginTop: 2 }}
                      >
                        su parte del cobro
                      </span>
                    </span>
                    <span style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span className="cw-mono" style={{ display: 'block', fontSize: 13.5, fontWeight: 700 }}>
                        {fmtClp(d.monto_clp)}
                      </span>
                      <span
                        style={{
                          display: 'block', fontSize: 11, fontWeight: 600,
                          color: e.color, marginTop: 2,
                        }}
                      >
                        {e.texto}
                      </span>
                    </span>
                  </span>
                );
              })}
            </span>

            {aMedias ? (
              <span className="cw-aviso media" style={{ marginTop: 11 }}>
                <span className="cw-ai"><span>!</span></span>
                <span>
                  Una pata salió y la otra no. El cobro cuadra y el giro no: falta
                  transferirle a {s.destinos.filter((d) => !d.girada)
                    .map((d) => d.compania_nombre ?? d.compania_id).join(' y ')}.
                </span>
              </span>
            ) : null}
          </div>
        );
      })}

      <div style={{ padding: '15px 24px', background: 'var(--cw-superficie)', fontSize: 12.5, color: 'var(--yool-gray1)' }}>
        {parciales > 0
          ? `${parciales} de ${splits.length} con el giro a medias. Es el descuadre del eje `
            + 'interno: el proveedor cobró bien, el reparto falló adentro.'
          : `${splits.length} ${splits.length === 1 ? 'cobro dividido' : 'cobros divididos'}, `
            + 'todos con sus dos patas en el mismo estado.'}
      </div>
    </div>
  );
}
